import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  FileText,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { InsuranceDataReadOnly } from "./InsuranceDataReadOnly";
import { FraudAnalysisPane } from "./FraudAnalysisPane";
import CONFIG from "../lib/api";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFOCRViewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();

  // State for PDF
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [viewMode, setViewMode] = useState("single");

  // State for data
  const [ocrData, setOcrData] = useState(null);
  const [llmData, setLlmData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [fraudAnalysisData, setFraudAnalysisData] = useState(null);
  const [documentDetails, setDocumentDetails] = useState(null);

  // State for UI
  const [editMode, setEditMode] = useState(false);
  const [showFraudAnalysis, setShowFraudAnalysis] = useState(true);
  const [loading, setLoading] = useState({
    pdf: false,
    ocr: false,
    llm: false,
    fraud: false,
    document: false,
  });
  const [errors, setErrors] = useState({
    pdf: null,
    ocr: null,
    llm: null,
    fraud: null,
    document: null,
  });

  const API_BASE_URL = CONFIG.baseUrl;

  useEffect(() => {
    if (documentId) {
      loadDocumentDetails(documentId);
      loadPdfUrl(documentId);
      loadDocumentData(documentId);
    }
  }, [documentId]);

  const loadDocumentDetails = async (docId) => {
    setLoading((prev) => ({ ...prev, document: true }));
    setErrors((prev) => ({ ...prev, document: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/documents/${docId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDocumentDetails(data.document);
    } catch (error) {
      console.warn('API not available, using mock data:', error);
      setDocumentDetails({
        id: docId,
        filename: "sample_document.pdf",
        blob_url: "/inv2.pdf",
        processing_status: "Processed",
        upload_date: new Date().toISOString(),
        file_size: "245KB"
      });
    } finally {
      setLoading((prev) => ({ ...prev, document: false }));
    }
  };

  const loadPdfUrl = async (docId) => {
    setLoading((prev) => ({ ...prev, pdf: true }));
    setErrors((prev) => ({ ...prev, pdf: null }));

    try {
      let response = await fetch(`${API_BASE_URL}/documents/${docId}/pdf-url`);

      if (response.ok) {
        const data = await response.json();
        setPdfUrl(data.pdf_url);
      } else {
        setPdfUrl(`${API_BASE_URL}/documents/${docId}/pdf-proxy`);
      }
    } catch (error) {
      console.error("Error loading PDF URL:", error);
    } finally {
      setLoading((prev) => ({ ...prev, pdf: false }));
    }
  };

  const loadDocumentData = async (docId) => {
    await loadOCRData(docId);
    await loadLLMData(docId);
    await loadFraudAnalysisData(docId);
  };

  const loadOCRData = async (docId) => {
    setLoading((prev) => ({ ...prev, ocr: true }));
    setErrors((prev) => ({ ...prev, ocr: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/documents/${docId}/ocr`);

      if (response.ok) {
        const data = await response.json();
        setOcrData(data);
      } else if (response.status === 404) {
        setOcrData(null);
      } else {
        throw new Error(`Failed to fetch OCR data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading OCR data:", error);
      setErrors((prev) => ({ ...prev, ocr: error.message }));

      try {
        const fallbackResponse = await fetch("/documents/100_pdf/ocr.json");
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setOcrData(fallbackData);
        }
      } catch (fallbackError) {
        console.error("Fallback OCR data also failed:", fallbackError);
      }
    } finally {
      setLoading((prev) => ({ ...prev, ocr: false }));
    }
  };

  const loadLLMData = async (docId) => {
    setLoading((prev) => ({ ...prev, llm: true }));
    setErrors((prev) => ({ ...prev, llm: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/documents/${docId}/llm-output`);

      if (response.ok) {
        const data = await response.json();
        setLlmData(data);
        setEditedData(data);
      } else if (response.status === 404) {
        setLlmData(null);
        setEditedData(null);
      } else {
        throw new Error(`Failed to fetch LLM data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading LLM data:", error);
      setErrors((prev) => ({ ...prev, llm: error.message }));

      try {
        const fallbackResponse = await fetch("/documents/100_pdf/llm_output.json");
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setLlmData(fallbackData);
          setEditedData(fallbackData);
        }
      } catch (fallbackError) {
        console.error("Fallback LLM data also failed:", fallbackError);
      }
    } finally {
      setLoading((prev) => ({ ...prev, llm: false }));
    }
  };

  const loadFraudAnalysisData = async (docId) => {
    setLoading((prev) => ({ ...prev, fraud: true }));
    setErrors((prev) => ({ ...prev, fraud: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/documents/${docId}/fraud-analysis`);

      if (response.ok) {
        const data = await response.json();
        setFraudAnalysisData(data);
      } else if (response.status === 404) {
        setFraudAnalysisData(null);
      } else {
        throw new Error(`Failed to fetch fraud analysis data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error loading fraud analysis data:", error);
      setErrors((prev) => ({ ...prev, fraud: error.message }));

      try {
        const fallbackResponse = await fetch("/documents/100_pdf/100_analysis.json");
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setFraudAnalysisData(fallbackData);
        }
      } catch (fallbackError) {
        console.error("Fallback fraud analysis data also failed:", fallbackError);
      }
    } finally {
      setLoading((prev) => ({ ...prev, fraud: false }));
    }
  };

  const handleBackToList = () => {
    navigate('/documents');
  };

  const extractTextValues = (obj, values = new Set()) => {
    if (typeof obj === "string" && obj.trim()) {
      const cleaned = obj.trim().toLowerCase();
      values.add(cleaned);
      values.add(cleaned.replace(/[\s-]/g, ""));
      cleaned.split(/[\s-]+/).forEach((word) => {
        if (word.length > 2) values.add(word);
      });
    } else if (typeof obj === "number") {
      values.add(obj.toString());
    } else if (typeof obj === "object" && obj !== null) {
      if (Array.isArray(obj)) {
        obj.forEach((item) => extractTextValues(item, values));
      } else {
        Object.values(obj).forEach((value) => extractTextValues(value, values));
      }
    }
    return values;
  };

  const isWordMatched = (word) => {
    if (!editedData) return false;

    const extractedValues = extractTextValues(editedData);
    const wordText = word.content.toLowerCase().trim();

    if (extractedValues.has(wordText)) return true;

    const cleanWord = wordText.replace(/[^\w]/g, "");
    if (cleanWord.length > 2 && extractedValues.has(cleanWord)) return true;

    if (wordText.length > 4) {
      for (const value of extractedValues) {
        if (value.includes(wordText) || wordText.includes(value)) {
          return true;
        }
      }
    }

    return false;
  };

  const renderBoundingBoxes = (pageNumber) => {
    if (!ocrData || !pageWidth || !pageHeight) return null;

    const pages = ocrData.pages || ocrData.analyzeResult?.readResults || [];
    const pageData = pages[pageNumber - 1];

    if (!pageData) return null;

    const words = pageData.words || [];

    if (!words.length) {
      return null;
    }

    const matchedWords = words.filter(isWordMatched);
    const dpi = 72;

    return matchedWords.map((word, index) => {
      const box = word.bounding_box;
      if (!box || box.length < 4) return null;

      const left = box[0].x * dpi * pdfScale;
      const top = box[0].y * dpi * pdfScale;
      const width = (box[1].x - box[0].x) * dpi * pdfScale;
      const height = (box[2].y - box[0].y) * dpi * pdfScale;

      const opacity = word.confidence || 0.5;

      return (
        <div
          key={`page-${pageNumber}-word-${index}`}
          className="absolute border-2 border-blue-500 bg-blue-200 pointer-events-none"
          style={{
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            opacity: opacity * 0.3,
            zIndex: 10,
          }}
          title={`${word.content} (confidence: ${(word.confidence * 100).toFixed(1)}%)`}
        />
      );
    });
  };

  const handleSave = () => {
    console.log("Saving data:", editedData);
    alert("Data saved! (Check console for details)");
  };

  const handleRefreshPdfUrl = async () => {
    if (documentId) {
      await loadPdfUrl(documentId);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    console.log(`PDF loaded with ${numPages} pages`);
  };

  const DataLoadingStatus = ({ type, loading, error, hasData }) => {
    if (loading) {
      return (
        <div className="flex items-center space-x-2 text-blue-600">
          <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading {type}...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center space-x-2 text-red-600">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{type} failed to load</span>
        </div>
      );
    }

    if (hasData) {
      return (
        <div className="flex items-center space-x-2 text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm">{type} loaded</span>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <FileText className="w-4 h-4" />
        <span className="text-sm">{type} not available</span>
      </div>
    );
  };

  const InsuranceDataEdit = ({ data, onDataChange }) => (
    <div className="bg-blue-50 border border-blue-200 rounded p-4">
      <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
      <button
        onClick={() => onDataChange({ ...data, edited: true })}
        className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
      >
        Simulate Edit
      </button>
    </div>
  );

  if (!documentId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <p className="text-gray-600">No document ID provided</p>
          <button
            onClick={handleBackToList}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-slate-800 text-white p-4 flex items-center shadow-md z-50">
        <button
          onClick={handleBackToList}
          className="mr-4 px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 transition-colors flex items-center space-x-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Documents</span>
        </button>
        <h1 className="text-xl font-semibold">
          PDF Analysis - {documentDetails?.filename || "Document"}
        </h1>
        <div className="ml-auto flex items-center space-x-4">
          <span className="text-sm text-gray-300">
            Document ID: {documentId}
          </span>
          {documentDetails?.processing_status && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              documentDetails.processing_status === 'Processed' 
                ? 'bg-green-600 text-white' 
                : documentDetails.processing_status === 'Pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-red-600 text-white'
            }`}>
              {documentDetails.processing_status}
            </span>
          )}
        </div>
      </div>

      {/* Main content with top padding for header */}
      <div className="flex w-full pt-16">
        {/* Left Panel - PDF Viewer */}
        <div className="w-1/2 p-4 bg-white border-r flex flex-col">
          {/* PDF Controls */}
          <div className="mb-4 flex-shrink-0">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold text-gray-800">Document View</h2>
              <div className="flex items-center space-x-2">
                <DataLoadingStatus
                  type="PDF"
                  loading={loading.pdf}
                  error={errors.pdf}
                  hasData={!!pdfUrl}
                />
                <DataLoadingStatus
                  type="OCR"
                  loading={loading.ocr}
                  error={errors.ocr}
                  hasData={!!ocrData}
                />
                <DataLoadingStatus
                  type="Extraction"
                  loading={loading.llm}
                  error={errors.llm}
                  hasData={!!llmData}
                />
                <DataLoadingStatus
                  type="Fraud"
                  loading={loading.fraud}
                  error={errors.fraud}
                  hasData={!!fraudAnalysisData}
                />

                <button
                  onClick={handleRefreshPdfUrl}
                  disabled={loading.pdf}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  title="Refresh PDF URL"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${loading.pdf ? "animate-spin" : ""}`}
                  />
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              {/* View Mode Toggle */}
              <div className="flex gap-2 items-center">
                <button
                  onClick={() =>
                    setViewMode(viewMode === "single" ? "all" : "single")
                  }
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                  title={
                    viewMode === "single" ? "Show All Pages" : "Show Single Page"
                  }
                >
                  {viewMode === "single" ? "Show All" : "Single Page"}
                </button>
              </div>

              {/* Zoom Controls */}
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setPdfScale((prev) => Math.max(0.5, prev - 0.1))}
                  className="p-2 bg-gray-200 rounded hover:bg-gray-300 flex items-center"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600 min-w-[60px] text-center">
                  {Math.round(pdfScale * 100)}%
                </span>
                <button
                  onClick={() => setPdfScale((prev) => Math.min(3, prev + 0.1))}
                  className="p-2 bg-gray-200 rounded hover:bg-gray-300 flex items-center"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>

              {/* Page Navigation */}
              {viewMode === "single" && numPages && numPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600 min-w-[80px] text-center">
                    Page {currentPage} of {numPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(numPages, prev + 1))
                    }
                    disabled={currentPage === numPages}
                    className="p-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {viewMode === "all" && numPages && (
                <div className="text-sm text-gray-600">
                  Total: {numPages} pages
                </div>
              )}
            </div>

            {pdfUrl && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                <div className="truncate">PDF URL: {pdfUrl}</div>
              </div>
            )}

            {errors.pdf && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">PDF loading failed</span>
                </div>
                <p className="text-xs text-red-600 mt-1">{errors.pdf}</p>
              </div>
            )}
          </div>

          {/* PDF Display */}
          <div className="flex-1 overflow-auto">
            <div className="relative">
              {loading.pdf ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-8 h-8 border border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading PDF URL...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => {
                    console.error("PDF load error:", error);
                    setErrors((prev) => ({
                      ...prev,
                      pdf: `Failed to load PDF: ${error.message}`,
                    }));
                  }}
                  loading={
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-gray-600">Loading PDF...</p>
                      </div>
                    </div>
                  }
                  error={
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center text-red-600">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                        <p>Failed to load PDF</p>
                        <p className="text-sm text-gray-500 break-all">
                          {pdfUrl}
                        </p>
                        <button
                          onClick={handleRefreshPdfUrl}
                          className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Retry Loading
                        </button>
                      </div>
                    </div>
                  }
                >
                  <div className="relative inline-block">
                    <Page
                      pageNumber={currentPage}
                      scale={pdfScale}
                      onLoadSuccess={(page) => {
                        setPageWidth(page.width);
                        setPageHeight(page.height);
                        console.log("Page loaded:", {
                          width: page.width,
                          height: page.height,
                          scale: pdfScale,
                        });
                      }}
                    />
                    <div className="absolute inset-0 pointer-events-none">
                      {renderBoundingBoxes(currentPage)}
                    </div>
                  </div>
                </Document>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p>No PDF available</p>
                    {documentId && (
                      <button
                        onClick={handleRefreshPdfUrl}
                        className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Load PDF
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Middle Panel - Extracted Data */}
        <div
          style={{
            width: showFraudAnalysis ? "33.333%" : "50%",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="p-4 pb-2" style={{ flexShrink: 0 }}>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Extracted Data</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFraudAnalysis(!showFraudAnalysis)}
                  className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  {showFraudAnalysis ? "Hide" : "Show"} Fraud Analysis
                </button>
                {editMode && llmData && (
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className="px-4 pb-4"
            style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
          >
            <div
              style={{ height: "100%", overflowY: "auto", paddingBottom: "20px" }}
            >
              {loading.llm && (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="w-6 h-6 border border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading extracted data...</p>
                  </div>
                </div>
              )}

              {errors.llm && !llmData && (
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <div className="flex items-center space-x-2 text-red-600 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Failed to load Extraction data</span>
                  </div>
                  <p className="text-sm text-red-600">{errors.llm}</p>
                </div>
              )}

              {!loading.llm && !llmData && !errors.llm && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <div className="flex items-center space-x-2 text-yellow-600 mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-medium">
                      No extracted data available
                    </span>
                  </div>
                  <p className="text-sm text-yellow-600">
                    Extraction may not be complete for this document yet.
                  </p>
                </div>
              )}

              {llmData && editedData && (
                <div className="space-y-4">
                  {editMode ? (
                    <InsuranceDataEdit
                      data={editedData}
                      onDataChange={setEditedData}
                    />
                  ) : (
                    <InsuranceDataReadOnly data={editedData} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Fraud Analysis */}
        {showFraudAnalysis && (
          <div className="w-1/3 border-l transition-all duration-300">
            {loading.fraud ? (
              <div className="p-4">
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="w-6 h-6 border border-gray-300 border-t-purple-600 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading fraud analysis...</p>
                  </div>
                </div>
              </div>
            ) : fraudAnalysisData ? (
              <FraudAnalysisPane analysisData={fraudAnalysisData} />
            ) : (
              <div className="p-4">
                <div className="bg-gray-50 border border-gray-200 rounded p-4">
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-medium">
                      No fraud analysis available
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Fraud analysis may not be complete for this document yet.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFOCRViewer;